'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X, ExternalLink, Play } from 'lucide-react'
import { linkClass } from 'app/components/styles'
import TrackedLink from 'app/components/tracked-link'

// Shared visual frame for case-study media, and the small caption below it.
const frameClass = 'rounded-[4px] w-full border border-[var(--text-secondary)]/10'
const captionClass = 'block text-xs text-[var(--text-secondary)] opacity-50 mt-2'
const triggerClass = 'block w-full cursor-zoom-in text-left hover:opacity-95 transition-opacity'
const enlargedClass = 'max-h-[85vh] w-auto max-w-[90vw] rounded-[4px] object-contain'

// Full-screen overlay, portaled to <body> so it escapes the article flow and
// any clipping/stacking ancestor. Dismiss via backdrop click, the X, or Escape.
function Lightbox({
  caption,
  onClose,
  children,
}: {
  caption?: string
  onClose: () => void
  children: ReactNode
}) {
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = 'unset'
      previouslyFocused?.focus()
    }
  }, [onClose])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={caption || 'Media'}
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <button
        ref={closeRef}
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 text-white/80 hover:text-white transition-colors"
        aria-label="Close"
      >
        <X size={24} strokeWidth={1.5} />
      </button>
      <div
        className="relative z-10 flex max-h-[90vh] max-w-[90vw] flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
        {caption && (
          <p className="mt-4 max-w-[90vw] text-center text-sm text-white/80">{caption}</p>
        )}
      </div>
    </div>,
    document.body
  )
}

// <Figure> — a case-study image. Click to open large; caption below.
export function MdxFigure({
  src,
  alt,
  caption,
  aspect,
}: {
  src: string
  alt?: string
  caption?: string
  // When set (e.g. "16 / 9"), the image is masked into a fixed-ratio box,
  // centred and contained (never cropped) — the frame fills the letterbox.
  aspect?: string
}) {
  const [open, setOpen] = useState(false)
  const label = caption ?? alt
  const altText = alt ?? caption ?? ''
  return (
    <figure className="my-6">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClass}
        aria-label={label ? `Expand image: ${label}` : 'Expand image'}
      >
        {aspect ? (
          <div
            className="w-full overflow-hidden rounded-[4px] border border-[var(--text-secondary)]/10"
            style={{ aspectRatio: aspect }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={src} alt={altText} className="h-full w-full object-cover" />
          </div>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={src} alt={altText} className={`${frameClass} h-auto`} />
        )}
      </button>
      {label && <figcaption className={captionClass}>{label}</figcaption>}
      {open && (
        <Lightbox caption={label} onClose={() => setOpen(false)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={src} alt={altText} className={enlargedClass} />
        </Lightbox>
      )}
    </figure>
  )
}

// <Clip> — a gif-style ambient clip: autoplays and loops. Click to open large
// (with controls in the lightbox). Download is disabled.
export function MdxClip({
  src,
  poster,
  caption,
}: {
  src: string
  poster?: string
  caption?: string
}) {
  const [open, setOpen] = useState(false)
  const [reduced, setReduced] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Respect prefers-reduced-motion: don't autoplay/loop the thumbnail; show a
  // static frame with a play affordance instead. Driven via ref (not the
  // autoPlay attribute) so there's no motion flash and no hydration mismatch.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => {
      setReduced(mq.matches)
      const v = videoRef.current
      if (!v) return
      v.muted = true
      if (mq.matches) {
        v.loop = false
        v.pause()
      } else {
        v.loop = true
        v.play().catch(() => {})
      }
    }
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  return (
    <figure className="my-6">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${triggerClass} relative`}
        aria-label={caption ? `Expand clip: ${caption}` : 'Expand clip'}
      >
        <video
          ref={videoRef}
          src={`${src}#t=0.1`}
          poster={poster}
          className={`${frameClass} pointer-events-none`}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        />
        {reduced && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-black/45 p-3 text-white backdrop-blur-sm">
              <Play size={20} strokeWidth={1.5} className="translate-x-px" />
            </span>
          </span>
        )}
      </button>
      {caption && <figcaption className={captionClass}>{caption}</figcaption>}
      {open && (
        <Lightbox caption={caption} onClose={() => setOpen(false)}>
          <video
            src={src}
            poster={poster}
            className={enlargedClass}
            autoPlay={!reduced}
            loop={!reduced}
            muted
            playsInline
            controls
            controlsList="nodownload"
            disablePictureInPicture
          />
        </Lightbox>
      )}
    </figure>
  )
}

// <Prototype> — an interactive HTML prototype embedded in a lazy iframe, with an
// "open full screen" link for the full experience.
export function MdxPrototype({
  src,
  href,
  title,
  caption,
  height = 640,
  aspect,
}: {
  src: string
  href?: string
  title?: string
  caption?: string
  height?: number
  // CSS aspect-ratio (e.g. "1280 / 1080"). When set, the frame scales
  // responsively at that ratio and `height` is ignored.
  aspect?: string
}) {
  return (
    <figure className="my-6">
      <div
        className="overflow-hidden rounded-[4px] border border-[var(--text-secondary)]/15 bg-[var(--bg-primary)]"
        style={aspect ? { aspectRatio: aspect } : { height: `${height}px` }}
      >
        <iframe
          src={src}
          title={title || caption || 'Interactive prototype'}
          loading="lazy"
          allowFullScreen
          className="block h-full w-full"
        />
      </div>
      <figcaption className={captionClass}>
        {caption ? `${caption} · ` : ''}
        {(href ?? src).startsWith('http') ? (
          // External (e.g. Figma): TrackedLink adds ?ref, analytics, and the icon.
          <TrackedLink
            href={href ?? src}
            name={title ? `prototype_${title}` : 'prototype_fullscreen'}
            className={linkClass}
          >
            Open full screen
          </TrackedLink>
        ) : (
          // Internal HTML prototype — open the local file in a new tab.
          <a
            href={href ?? src}
            target="_blank"
            rel="noopener noreferrer"
            className={`${linkClass} inline-flex items-center`}
          >
            Open full screen
            <ExternalLink className="ml-1 inline-block align-baseline" size={12} strokeWidth={1.5} />
          </a>
        )}
      </figcaption>
    </figure>
  )
}

// <Player> — a narrated demo: native controls + fullscreen, download disabled.
export function MdxPlayer({
  src,
  poster,
  caption,
}: {
  src: string
  poster?: string
  caption?: string
}) {
  return (
    <figure className="my-6">
      <video
        src={src}
        poster={poster}
        className={frameClass}
        controls
        controlsList="nodownload"
        disablePictureInPicture
        playsInline
        preload="metadata"
        aria-label={caption}
      />
      {caption && <figcaption className={captionClass}>{caption}</figcaption>}
    </figure>
  )
}
